// ─────────────────────────────────────────────────────────────────────────────
// DrawingsTable — reusable table for showing drawings on Job Detail,
// Fabricated Item detail, and the standalone /documents library page.
//
// Defensive: renders a clear empty-state message if the backend endpoint
// isn't wired yet (notAvailable=true) vs. truly empty (documents=[]).
// Uses the PDF-guarded preview pattern from isPdfUrl — same rendering
// approach as the Task edit dialog post-PR#4.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import {
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  Paper, Chip, Typography, Box, Alert, IconButton, Tooltip, Button,
} from "@mui/material";
import {
  OpenInNew as OpenInNewIcon,
  PictureAsPdfOutlined,
  Inventory2Outlined,
  RestoreFromTrashOutlined,
  ApprovalOutlined,
} from "@mui/icons-material";
import { isPdfUrl } from "../../utils/isPdfUrl";
import prettifyDocumentName from "../../utils/prettifyDocumentName";
import LifecycleStatusBadge from "../LifecycleStatus/LifecycleStatusBadge";
import { openAuthedMedia } from "../AuthedMedia/AuthedMedia";
import { fmtDateLong, fmtDateISO } from "../../utils/dateFmt";
import EmptyState from "../EmptyState/EmptyState";
import SmartTable from "../SmartTable/SmartTable";

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Backward-compat shim — earlier callers still reference formatDate.
// CR-2026-FE-DATE-FMT-CONSOLIDATION 2026-05-06: prefer fmtDateLong /
// fmtDateISO from utils/dateFmt directly going forward.
const formatDate = (iso) => fmtDateLong(iso, "");

// CR-2026-DOCUMENTS-ARCHIVE-RESTORE 2026-05-05 — accept an `archived` flag
// computed from doc.deleted_at / deletedAt at the row level. When true, the
// chip shows a grey "archived" badge regardless of the doc's stored
// approval_status, because the row is currently hidden from the active list.
function StatusChip({ status, archived = false }) {
  if (archived) {
    return (
      <Chip
        size="small"
        variant="outlined"
        color="default"
        sx={{ color: "text.secondary", borderColor: "grey.500" }}
        label="archived"
      />
    );
  }
  const color = {
    approved: "success",
    pending: "warning",
    superseded: "default",
    rejected: "error",
  }[status] || "default";
  return (
    <Chip
      size="small"
      variant="outlined"
      color={color}
      label={(status || "unknown").replace("_", " ")}
    />
  );
}

// Helper — check both common shapes for the soft-delete timestamp field.
// Backend may emit `deletedAt` (camelCase, paranoid:true Sequelize default
// on the live droplet model) OR `deleted_at` (snake_case, used in some
// places of the new src/ tree). Either being non-null means archived.
function isArchived(d) {
  return !!(d && (d.deletedAt || d.deleted_at));
}

/**
 * @param {object} props
 * @param {Array} props.documents  — list of document records
 * @param {boolean} [props.notAvailable] — true when backend endpoint returned 404
 * @param {boolean} [props.loading]
 * @param {Error|null} [props.error]
 * @param {string} [props.emptyMessage='No drawings uploaded yet.']
 * @param {boolean} [props.showJob]  — when true, show Job column (for library view)
 * @param {boolean} [props.showPmk=true]
 * @param {boolean} [props.showFabItem=false]
 * @param {(document: object) => void} [props.onDelete] — when provided, renders
 *        an Archive IconButton on active rows. Disabled for approved drawings
 *        (records preservation). Caller is responsible for opening the
 *        DeleteDocumentDialog and refreshing the list on success.
 *        (Action surface renamed Delete → Archive 2026-05-05 per
 *        CR-2026-DOCUMENTS-ARCHIVE-RESTORE; prop name kept for stability.)
 * @param {(document: object) => void} [props.onRestore] — when provided AND
 *        the row is archived (deletedAt non-null), renders a Restore
 *        IconButton in place of the Archive button. Caller is responsible
 *        for the PATCH call and refreshing the list on success.
 *        (CR-2026-DOCUMENTS-ARCHIVE-RESTORE 2026-05-05.)
 */
export default function DrawingsTable({
  documents = [],
  notAvailable = false,
  loading = false,
  error = null,
  emptyMessage = "No drawings uploaded yet.",
  showJob = false,
  showPmk = true,
  showFabItem = false,
  onDelete = null,
  onRestore = null,
  onRecordEorStamp = null,
  onManageLifecycle = null,
}) {
  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">Loading drawings…</Typography>
      </Box>
    );
  }

  if (notAvailable) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Drawings library not yet live
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          This page is wired up on the frontend, but the backend
          <code> /api/document </code> endpoint hasn't been deployed yet.
          Upload works (files are stored), but listing requires the backend
          change tracked in{" "}
          <code>outputs/SFM-drawings-backend-spec.md</code>. Falling back to
          per-job / per-task drawings where available.
        </Typography>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Could not load drawings. {String(error?.message || error).slice(0, 200)}
      </Alert>
    );
  }

  if (!documents.length) {
    // CR-2026-FE-POLISH-BATCH-2 2026-05-06: standardized EmptyState.
    return (
      <EmptyState
        icon={PictureAsPdfOutlined}
        title={emptyMessage || "No documents yet"}
        description="Upload a drawing package to get started, or adjust your filters to see more results."
        size="md"
      />
    );
  }

  return (
    // CR-2026-FE-POLISH-BATCH-2 2026-05-06: horizontal scroll on the table.
    // After the upload-metadata CR added 5 new columns (Sheets/Issued/
    // Addendas/Fab Cat/Detailer), DrawingsTable is at 16 columns and
    // overflowed clipped on most laptop widths. The Paper wrapper now allows
    // overflow-x:auto so the user can scroll horizontally to reach the
    // Action column on narrower screens.
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, overflowX: "auto" }}>
      <SmartTable tableId="drawings" lockedColumns={["File", "Rev", "Status"]} minWidths={{ Action: 300 }} size="small" sx={{ minWidth: 1400 }}>
        <TableHead>
          <TableRow>
            <TableCell>File</TableCell>
            {showJob && <TableCell>Job</TableCell>}
            {showPmk && <TableCell>PMK</TableCell>}
            {showFabItem && <TableCell>Item</TableCell>}
            <TableCell>Rev</TableCell>
            <TableCell>Type</TableCell>
            {/* CR-2026-FE-DOC-UPLOAD-METADATA 2026-05-05 — 5 upload-time snapshot columns */}
            <TableCell>Sheets</TableCell>
            <TableCell>Issued</TableCell>
            <TableCell>Addendas</TableCell>
            <TableCell>Fab Cat</TableCell>
            <TableCell>Detailer</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Size</TableCell>
            <TableCell>Uploaded</TableCell>
            <TableCell align="right">Open</TableCell>
            {(onDelete || onRestore || onRecordEorStamp || onManageLifecycle) && <TableCell align="right" sx={{ whiteSpace: "nowrap", minWidth: 300 }}>Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((d) => {
            const url = d.file_url || d.url;
            const isPdf = isPdfUrl(url);
            const archived = isArchived(d);
            const prettyName = prettifyDocumentName(d);
            return (
              <TableRow
                key={d.id}
                hover
                sx={archived ? { opacity: 0.7, backgroundColor: "action.hover" } : undefined}
              >
                <TableCell sx={{ maxWidth: 360 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    {isPdf && <PictureAsPdfOutlined fontSize="small" color="action" sx={{ flexShrink: 0 }} />}
                    <Tooltip title={prettyName} placement="top" arrow disableInteractive>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        {prettyName}
                      </Typography>
                    </Tooltip>
                  </Box>
                </TableCell>
                {showJob && (
                  <TableCell>
                    {/* Prefer job_name if backend joined it; otherwise show
                        the integer job_id prefixed so the user sees it's an
                        ID. The Documents Library page enriches docs with
                        job_name from its loaded jobs map (see
                        DocumentsLibrary.jsx). */}
                    {d.job_name || (d.job_id != null ? `Job #${d.job_id}` : "—")}
                  </TableCell>
                )}
                {showPmk && <TableCell>{d.pmk_number || d.pmk || "—"}</TableCell>}
                {showFabItem && (
                  <TableCell>{d.fabricated_item_name || d.fabricated_item_id || "—"}</TableCell>
                )}
                <TableCell>
                  <Chip size="small" label={d.revision || "—"} variant="outlined" />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{d.doc_type || "—"}</Typography>
                </TableCell>
                {/* CR-2026-FE-DOC-UPLOAD-METADATA 2026-05-05 — 5 upload-time snapshot cells */}
                <TableCell>
                  <Typography variant="caption">
                    {d.sheet_count != null ? `${d.sheet_count}` : "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{fmtDateISO(d.issued_at)}</Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={d.addendas || ""} placement="top" arrow disableInteractive>
                    <Typography variant="caption" sx={{ display: "inline-block", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle" }}>
                      {d.addendas || "—"}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {d.fabrication_category_snapshot ? (
                    <Chip
                      size="small"
                      label={d.fabrication_category_snapshot}
                      sx={{ bgcolor: "#e7f1ff", color: "#0066cc", fontWeight: 600, fontSize: "0.65rem" }}
                    />
                  ) : (
                    <Typography variant="caption">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={d.detailer_name_snapshot || ""} placement="top" arrow disableInteractive>
                    <Typography variant="caption" sx={{ display: "inline-block", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle" }}>
                      {d.detailer_name_snapshot || "—"}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <StatusChip status={d.approval_status || "approved"} archived={archived} />
                  {d.lifecycle_status && (
                    <Box sx={{ mt: 0.5 }}>
                      <LifecycleStatusBadge status={d.lifecycle_status} />
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {formatBytes(d.file_size_bytes)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(d.uploaded_at)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {url && (
                    <Tooltip title={isPdf ? "Open drawing PDF" : "Open"}>
                      {/* CA-5 companion: /uploads/documents is auth-gated, so a plain
                          href opens a 401. Fetch with the Bearer token + open a blob. */}
                      <IconButton
                        onClick={() => openAuthedMedia(url)}
                        size="small"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
                {(onDelete || onRestore || onRecordEorStamp || onManageLifecycle) && (
                  <TableCell align="right" sx={{ whiteSpace: "nowrap", minWidth: 300 }}>
                    {onManageLifecycle && !archived && (
                      <Tooltip title="Manage the D-032 14-state document lifecycle (received → approved → current for fabrication → superseded …)">
                        <span>
                          <Button size="small" variant="text" onClick={() => onManageLifecycle(d)} sx={{ mr: 0.5, whiteSpace: "nowrap" }}>
                            Lifecycle
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {onRecordEorStamp && !archived && ["engineering_package", "shop_drawing"].includes(d.doc_type) && (
                      ["APPROVED", "APPROVED_AS_NOTED", "CURRENT_FOR_FABRICATION", "CURRENT_FOR_ERECTION", "CURRENT_WITH_CORRECTIONS"].includes(d.lifecycle_status || "RECEIVED")
                        ? <Chip size="small" color="success" label={(d.lifecycle_status === "APPROVED_AS_NOTED") ? "EOR \u2713 as noted" : "EOR \u2713"} sx={{ mr: 0.5, flexShrink: 0 }} />
                        : (
                          <Tooltip title="Record the Engineer-of-Record \u00a71.7.0 approval stamp on this structural drawing">
                            <span>
                              <Button size="small" variant="outlined" color="success" startIcon={<ApprovalOutlined fontSize="small" />} onClick={() => onRecordEorStamp(d)} sx={{ mr: 0.5, whiteSpace: "nowrap", flexShrink: 0, minWidth: "max-content" }}>
                                EOR Stamp
                              </Button>
                            </span>
                          </Tooltip>
                        )
                    )}
                    {(() => {
                      const status = d.approval_status || "approved";
                      const isApproved = status === "approved";
                      // ARCHIVED ROW — show restore icon when onRestore is wired.
                      if (archived && onRestore) {
                        return (
                          <Tooltip title="Restore document — clears archived flag and returns it to the active list">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onRestore(d)}
                              >
                                <RestoreFromTrashOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        );
                      }
                      // ARCHIVED ROW BUT NO RESTORE HANDLER — disable button entirely.
                      if (archived) {
                        return (
                          <Tooltip title="This row is archived. Pass an onRestore handler to enable restore.">
                            <span>
                              <IconButton size="small" disabled>
                                <RestoreFromTrashOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        );
                      }
                      // ACTIVE ROW — show archive icon (CR-2026-DOCUMENTS-ARCHIVE-RESTORE
                      // renamed from Delete → Archive).
                      if (onDelete) {
                        return (
                          <Tooltip
                            title={
                              isApproved
                                ? "Approved drawings cannot be archived (records preservation per AISC 303 §4)"
                                : "Archive document"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="warning"
                                disabled={isApproved}
                                onClick={() => onDelete(d)}
                              >
                                <Inventory2Outlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        );
                      }
                      return null;
                    })()}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </SmartTable>
    </TableContainer>
  );
}
